import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";

const AlphabetSortWidget = ({
  onSelectLetter,
  selectedLetter: controlledLetter,
  sortOrder = "asc",
  onSortToggle,
  viewMode,
  onViewModeChange,
}) => {
  const [internalLetter, setInternalLetter] = useState(null);
  const activeLetter =
    controlledLetter !== undefined ? controlledLetter : internalLetter;

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const handleLetterClick = (letter) => {
    const newSelected = activeLetter === letter ? null : letter;
    if (controlledLetter === undefined) {
      setInternalLetter(newSelected);
    }
    onSelectLetter?.(newSelected);
  };

  const handleAllClick = () => {
    if (controlledLetter === undefined) {
      setInternalLetter(null);
    }
    onSelectLetter?.(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 1.25 },
        borderRadius: 3,
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
        width: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 1.5 }}
        sx={{ width: "100%" }}
      >
        {/* Sort order toggle button */}
        {onSortToggle && (
          <Tooltip
            title={`Sorting: ${
              sortOrder === "asc" ? "A to Z (Ascending)" : "Z to A (Descending)"
            }`}
          >
            <Button
              onClick={onSortToggle}
              variant="contained"
              size="small"
              startIcon={<SortByAlphaIcon sx={{ fontSize: "1.1rem" }} />}
              endIcon={
                sortOrder === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: "0.9rem" }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: "0.9rem" }} />
                )
              }
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                px: { xs: 1.2, sm: 1.8 },
                height: 36,
                bgcolor: "#0f172a",
                color: "#ffffff",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.15)",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: "#1e293b",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.25)",
                },
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
            >
              {sortOrder === "asc" ? "A to Z" : "Z to A"}
            </Button>
          </Tooltip>
        )}

        {/* Letters bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", lg: "center" },
            overflowX: "auto",
            whiteSpace: "nowrap",
            gap: { xs: 0.25, sm: 0.35, md: 0.45, lg: 0.5 },
            py: 0.25,
            px: 0.25,
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": { height: 3 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd5e1",
              borderRadius: 2,
            },
            flexGrow: 1,
            minWidth: 0, // Crucial for flex child overflow handling
          }}
        >
          {/* ALL Option */}
          <Button
            size="small"
            onClick={handleAllClick}
            sx={{
              minWidth: { xs: 34, sm: 36, md: 38 },
              height: 32,
              px: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              fontWeight: !activeLetter ? 800 : 600,
              borderRadius: "8px",
              flexShrink: 0,
              bgcolor: !activeLetter ? "#0288d1" : "#f1f5f9",
              color: !activeLetter ? "#ffffff" : "#475569",
              border: !activeLetter ? "none" : "1px solid #e2e8f0",
              boxShadow: !activeLetter
                ? "0 2px 8px rgba(2, 136, 209, 0.3)"
                : "none",
              "&:hover": {
                bgcolor: !activeLetter ? "#0277bd" : "#e2e8f0",
                color: !activeLetter ? "#ffffff" : "#0f172a",
              },
              transition: "all 0.15s ease-in-out",
            }}
          >
            ALL
          </Button>

          {alphabet.map((letter) => {
            const isSelected = activeLetter === letter;
            return (
              <Button
                key={letter}
                size="small"
                onClick={() => handleLetterClick(letter)}
                sx={{
                  minWidth: { xs: 22, sm: 24, md: 26, lg: 28 },
                  width: { xs: 22, sm: 24, md: 26, lg: 28 },
                  height: 32,
                  p: 0,
                  fontSize: { xs: "0.72rem", sm: "0.76rem", md: "0.8rem" },
                  fontWeight: isSelected ? 800 : 600,
                  borderRadius: "8px",
                  flexShrink: 0,
                  bgcolor: isSelected ? "#0288d1" : "#f8fafc",
                  color: isSelected ? "#ffffff" : "#334155",
                  border: isSelected
                    ? "1px solid #0288d1"
                    : "1px solid #e2e8f0",
                  boxShadow: isSelected
                    ? "0 2px 8px rgba(2, 136, 209, 0.35)"
                    : "none",
                  "&:hover": {
                    bgcolor: isSelected ? "#0277bd" : "#eff6ff",
                    borderColor: isSelected ? "#0277bd" : "#93c5fd",
                    color: isSelected ? "#ffffff" : "#0288d1",
                  },
                  transition: "all 0.15s ease-in-out",
                }}
              >
                {letter}
              </Button>
            );
          })}
        </Box>

        {/* Optional View Mode toggle */}
        {viewMode && onViewModeChange && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) onViewModeChange(newMode);
            }}
            aria-label="view mode"
            size="small"
            sx={{
              backgroundColor: "#f8fafc",
              borderRadius: "10px",
              height: 36,
              flexShrink: 0,
              border: "1px solid #e2e8f0",
              "& .MuiToggleButton-root": {
                borderRadius: "8px",
                px: 1.25,
                border: "none",
                color: "#64748b",
                "&.Mui-selected": {
                  bgcolor: "#0f172a",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1e293b" },
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
    </Paper>
  );
};

export default AlphabetSortWidget;
